import React, { useState, useEffect } from "react";
import AddOnsForm from "./AddOnsForm";
import api from "../../services/api";
import toast from "react-hot-toast";

const AddOnsManagement = () => {
  const [addOns, setAddOns] = useState([]);
  const [editingAddOn, setEditingAddOn] = useState(null);
  const [showAddOnForm, setShowAddOnForm] = useState(false);

  useEffect(() => {
    if (!showAddOnForm) {
      fetchAddOns();
    }
  }, [showAddOnForm]);

  const fetchAddOns = async () => {
    try {
      const response = await api.get("/addons");
      if (response.data.success) {
        setAddOns(response.data.data);
      }
    } catch (error) {
      console.error("Fetch Add-ons Error:", error);
      toast.error("Failed to fetch add-ons");
    }
  };

  const handleDeleteAddOn = async (id) => {
    if (window.confirm("Are you sure you want to delete this add-on configuration?")) {
      try {
        const response = await api.delete(`/addons/${id}`);
        if (response.data.success) {
          toast.success("Add-on configuration deleted");
          fetchAddOns();
        }
      } catch (error) {
        console.error("Delete Add-on Error:", error);
        toast.error("Failed to delete add-on configuration");
      }
    }
  };

  const handleAddOnFormSuccess = () => {
    setShowAddOnForm(false);
    setEditingAddOn(null);
    fetchAddOns();
  };

  if (showAddOnForm) {
    return (
      <AddOnsForm
        initialData={editingAddOn}
        isEdit={!!editingAddOn}
        onCancel={() => {
          setShowAddOnForm(false);
          setEditingAddOn(null);
        }}
        onSuccess={handleAddOnFormSuccess}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          Add-on Configurations
        </h2>
        <button
          onClick={() => {
            setEditingAddOn(null);
            setShowAddOnForm(true);
          }}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
        >
          + Add New Configuration
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {addOns.map((addon) => (
          <div
            key={addon._id}
            className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 p-4 flex flex-col"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">
                  {addon.brand.replace("_", " ").toUpperCase()}
                </h3>
                <span className="inline-block px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-600 rounded mt-1">
                  {Array.isArray(addon.category)
                    ? addon.category.join(", ")
                    : addon.category}
                </span>
              </div>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                {addon.groups.length} Groups
              </span>
            </div>
            
            <div className="flex-1 mb-4">
              <p className="text-sm text-gray-500 mb-2">Groups:</p>
              <ul className="text-sm text-gray-700 space-y-1">
                {addon.groups.slice(0, 3).map((group, idx) => (
                  <li key={idx} className="flex justify-between">
                    <span>{group.name}</span>
                    <span className="text-xs text-gray-500">
                      {group.items.length} items
                    </span>
                  </li>
                ))}
                {addon.groups.length > 3 && (
                  <li className="text-gray-400 text-xs pt-1">
                    + {addon.groups.length - 3} more groups
                  </li>
                )}
              </ul>
            </div>

            <div className="flex gap-2 mt-auto">
              <button
                onClick={() => {
                  setEditingAddOn(addon);
                  setShowAddOnForm(true);
                }}
                className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition font-medium text-sm"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteAddOn(addon._id)}
                className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition font-medium text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      {addOns.length === 0 && (
        <div className="text-center py-10 text-gray-500">
          No add-on configurations found. Create one to get started!
        </div>
      )}
    </div>
  );
};

export default AddOnsManagement;
